import { sleep } from "./schedule";

export async function retry<T>(
  fn: (attempt: number) => Promise<T>,
  max: number,
  delay?: Temporal.Duration | ((count: number) => Temporal.Duration),
): Promise<T> {
  for (let count = 1; count < max; count++) {
    try {
      return await fn(count);
    } catch {
      //
    }
    if (delay) {
      await sleep(typeof delay === "function" ? delay(count) : delay);
    }
  }
  return await fn(max);
}

export interface BackoffDelayOptions {
  baseDelay: Temporal.Duration;
  maxDelay: Temporal.Duration;
  jitterFactor?: number;
}

export interface ExponentialBackoffOptions extends BackoffDelayOptions {
  maxAttempts: number;
  shouldRetry?: (error: any) => boolean;
  signal?: AbortSignal;
}

/** `baseDelay * 2^attempt`, capped at `maxDelay`, ± `jitterFactor`, in whole milliseconds. */
export function exponentialBackoffDelay(
  attempt: number,
  { baseDelay, maxDelay, jitterFactor = 0.1 }: BackoffDelayOptions,
): Temporal.Duration {
  const cappedDelay = Math.min(
    baseDelay.total("milliseconds") * 2 ** attempt,
    maxDelay.total("milliseconds"),
  );

  // Add jitter to avoid thundering herd: delay ± (delay * jitterFactor)
  const jitter = cappedDelay * jitterFactor * (2 * Math.random() - 1);
  const finalDelay = Math.max(0, cappedDelay + jitter);

  // Temporal.Duration rejects fractional milliseconds.
  return Temporal.Duration.from({ milliseconds: Math.floor(finalDelay) });
}

export async function exponentialBackoffRetry<T>(
  fn: () => Promise<T>,
  options: ExponentialBackoffOptions,
): Promise<T> {
  const {
    maxAttempts,
    baseDelay = Temporal.Duration.from({ milliseconds: 100 }),
    maxDelay = Temporal.Duration.from({ seconds: 5 }),
    jitterFactor = 0.1,
    signal,
  } = options;

  for (let attempt = 1; attempt < maxAttempts; attempt++) {
    signal?.throwIfAborted();
    try {
      return await fn();
    } catch (error) {
      signal?.throwIfAborted();
      if (options.shouldRetry && !options.shouldRetry(error)) {
        throw error;
      }
      await sleep(
        exponentialBackoffDelay(attempt, { baseDelay, maxDelay, jitterFactor }),
        signal,
      );
    }
  }
  signal?.throwIfAborted();
  return await fn();
}
